import { ChangeDetectionStrategy, Component, EventEmitter, HostBinding, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { ParticipantMarketType } from '@cds/betting-offer';
import { MarketSubType, MarketType } from '@frontend/sports/betting-offer/feature/types';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { NgxFloatUiPlacements } from 'ngx-float-ui';
import { EpcotConfigService, EpcotModule } from 'packages/sports/web/app/src/common/epcot-config.service';

import { SportcastOption } from '../../../core/picks/betslip-bet-builder-pick';
import { BaseBetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { PickOddsState } from '../../../core/picks/pick-models';
import { BetBuilderSelectionModel } from './betbuilder-selection.model';

@Component({
    selector: 'bs-betslip-betbuilder-selection',
    templateUrl: './betslip-betbuilder-selection.component.html',
    styleUrls: ['./betslip-betbuilder-selection.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class BetslipBetbuilderSelectionComponent implements OnChanges {
    @HostBinding('class.market-leg') marketLeg = true;

    @Input() pick: BaseBetslipGroupPick;
    @Input() selection: BetBuilderSelectionModel;
    @Input() showRemoveButton: boolean;

    @Output() selectionRemove = new EventEmitter<SportcastOption>();
    readonly messages: {
        closed: string;
    };

    pickOddsState = PickOddsState;
    signpostingData?: {
        text: string | undefined;
        marketType: MarketType | ParticipantMarketType | undefined;
        marketSubType: MarketSubType | undefined;
    };

    constructor(
        private epcotConfigService: EpcotConfigService,
        public sitecore: Sitecore,
    ) {
        this.messages = {
            closed: this.sitecore.betslip.messages.ClosedPick,
        };
    }

    ngOnChanges(changes: SimpleChanges): void {
        const selectionChanges = changes.selection;

        if (selectionChanges) {
            const currentSelection = selectionChanges.currentValue;

            if (!currentSelection) {
                this.signpostingData = undefined;

                return;
            }

            const previousSelection = selectionChanges.previousValue;
            // Check if this is the first change or if any relevant property has changed
            const hasRelevantChange =
                selectionChanges.isFirstChange() || previousSelection?.marketSubTypeSignpostingText !== currentSelection.marketSubTypeSignpostingText;

            if (hasRelevantChange) {
                const leg = this.pick.getLegs().find((l) => l.id.id === this.selection.longId);

                this.signpostingData = {
                    text: this.selection.marketSubTypeSignpostingText,
                    marketType: leg?.market.marketType,
                    marketSubType: leg?.market.marketSubType,
                };
            }
        }
    }

    get isEpcotEnabled() {
        return this.epcotConfigService.isEnabled(EpcotModule.MyBetsBetSlip);
    }

    get isRemoveButtonVisible(): boolean {
        return this.isEpcotEnabled || this.showRemoveButton;
    }

    removeSelection(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.selectionRemove.emit(this.selection);
    }

    get selectionName(): string {
        return this.selection.name;
    }

    get hasWarning(): boolean {
        return this.selection.hasWarning ?? false;
    }

    get oddsState(): PickOddsState {
        return this.selection.oddsState ?? PickOddsState.Open;
    }

    protected readonly NgxFloatUiPlacements = NgxFloatUiPlacements;
}
